import { type ComponentType, lazy } from "react";

export type PreloadablePage = ComponentType & { preload: () => Promise<unknown> };

/** Route-level code splitting with an explicit `preload()` for link hover and idle time. */
function lazyPage<M extends Record<string, unknown>>(load: () => Promise<M>, exportName: keyof M) {
	let promise: Promise<M> | undefined;
	const preload = () => {
		promise ??= load();
		return promise;
	};
	const Component = lazy(() =>
		preload().then((mod) => ({ default: mod[exportName] as ComponentType })),
	);
	return Object.assign(Component, { preload }) as PreloadablePage;
}

export const pages = {
	login: lazyPage(() => import("@/pages/login-page"), "LoginPage"),
	overview: lazyPage(() => import("@/pages/overview-page"), "OverviewPage"),
	tenants: lazyPage(() => import("@/pages/tenants/tenants-page"), "TenantsPage"),
	tenant: lazyPage(() => import("@/pages/tenants/tenant-page"), "TenantPage"),
	users: lazyPage(() => import("@/pages/users/users-page"), "UsersPage"),
	user: lazyPage(() => import("@/pages/users/user-page"), "UserPage"),
	admins: lazyPage(() => import("@/pages/admins/admins-page"), "AdminsPage"),
	catalog: lazyPage(() => import("@/pages/catalog/catalog-page"), "CatalogPage"),
	payments: lazyPage(() => import("@/pages/payments-page"), "PaymentsPage"),
	audit: lazyPage(() => import("@/pages/audit-page"), "AuditPage"),
	notFound: lazyPage(() => import("@/pages/not-found-page"), "NotFoundPage"),
};

export function preloadWhenIdle(list: PreloadablePage[]) {
	const run = () => {
		for (const page of list) void page.preload();
	};
	if ("requestIdleCallback" in window) window.requestIdleCallback(run, { timeout: 3000 });
	else setTimeout(run, 1500);
}
